import { getItemEncrypted } from './encrypted-local-storage.js';
import { encryptData, decryptData } from './encrypt-service.js';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';
import { exportRoutingConfig, importRoutingConfig } from './routing-config-manager.js';

export const downloadConfigAsJson = async (showSuccess, showError, t) => {
  try {
    // Exporter la configuration de routage (nouvelle structure)
    const routingConfigExport = await exportRoutingConfig();

    // Créer la configuration complète avec les deux systèmes
    const configData = {
      // Métadonnées du fichier
      _metadata: {
        version: '2.1',
        exported_at: new Date().toISOString(),
        encryption: 'enabled',
        note: 'API keys are encrypted, routing config uses new unified structure'
      },

      // Langues
      ui_language: localStorage.getItem('ui_language'),

      // Nouvelle structure unifiée de routage
      // La langue de travail IA est stockée dans routing_config.text['X-Working-Language']
      routing_config: routingConfigExport.routing_config,

      // Données World Anvil
      wa_world: localStorage.getItem('wa_world'),
      wa_manuscript: localStorage.getItem('wa_manuscript'),
      wa_manuscript_labels: localStorage.getItem('wa_manuscript_labels'),
      wa_version: localStorage.getItem('wa_version'),
      wa_label_generated: localStorage.getItem('wa_label_generated'),
      wa_label_copied: localStorage.getItem('wa_label_copied'),
      wa_label_canon: localStorage.getItem('wa_label_canon'),

      // Données spécifiques Bellow
      bel_tags: localStorage.getItem('bel_tags'),
      bel_shortcuts: localStorage.getItem('bel_shortcuts'),
      bel_formats: localStorage.getItem('bel_formats')
    };
    
    // Convertir en JSON
    const jsonString = JSON.stringify(configData, null, 2);
    
    // Créer un blob et générer un URL
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Créer un élément <a> pour le téléchargement
    const a = document.createElement('a');
    a.href = url;
    a.download = `wa-bellow-config-encrypted-${new Date().toISOString().split('T')[0]}.json`;
    
    // Déclencher le téléchargement
    document.body.appendChild(a);
    a.click();
    
    // Nettoyer
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showSuccess(t('CONFIG', 'CONFIG_FILE_DOWNLOADED', 'Encrypted configuration file downloaded'));
  } catch (error) {
    debug.error(config, 'Erreur lors du téléchargement de la configuration:', error);
    showError(t('CONFIG', 'CONFIG_DOWNLOAD_ERROR', 'Error downloading configuration file'));
  }
};

export const uploadConfigFile = async (
  event,
  setValue,
  language,
  changeLanguage,
  setEncryptedApiKey,
  getCircuitBreaker,
  HEALTHCHECK_MODE,
  showSuccess,
  showError,
  t
) => {
  const fileInput = event.target;
  const file = fileInput.files[0];
  
  if (!file) {
    return;
  }
  
  const reader = new FileReader();
  
  reader.onload = async (e) => {
    try {
      const configData = JSON.parse(e.target.result);
      
      // Vérifier le format du fichier (supporte v2.0 et v2.1)
      if (!configData._metadata || !['2.0', '2.1'].includes(configData._metadata.version) || configData._metadata.encryption !== 'enabled') {
        throw new Error('Format de fichier non supporté. Seul le format v2.0+ encrypté est accepté.');
      }
      
      debug.log(config, `Chargement de la configuration encryptée v${configData._metadata.version}`);

      // Importer la configuration de routage (nouvelle structure) si présente
      if (configData.routing_config) {
        const routingImported = await importRoutingConfig(configData);
        if (!routingImported) {
          debug.warn(config, 'Impossible d\'importer la configuration de routage');
        }
      }

      // Mettre à jour le localStorage avec les données du fichier (non-encryptées)
      const nonEncryptedKeys = [
        // Langues
        'ui_language',
        // La langue de travail IA est dans routing_config.text['X-Working-Language']
        // données World Anvil
        'wa_world',
        'wa_manuscript',
        'wa_manuscript_labels',
        'wa_version',
        'wa_label_generated',
        'wa_label_copied',
        'wa_label_canon',
        // données Bellow
        'bel_tags',
        'bel_shortcuts',
        'bel_formats'
       ];
      
      nonEncryptedKeys.forEach(key => {
        if (configData[key] !== null && configData[key] !== undefined) {
          localStorage.setItem(key, configData[key]);
        }
      });

      // Décrypter les clés API depuis routing_config
      let decryptedApiKeys = {};
      debug.log(config, 'Décryptage des clés API depuis routing_config...');
      
      const sectionMap = [
        { section: 'text', formField: 'textAiApiKey' },
        { section: 'vision', formField: 'visionAiApiKey' },
        { section: 'translation', formField: 'translationAiApiKey' }
      ];
      
      // Importer la configuration de routage d'abord pour avoir accès aux clés
      if (configData.routing_config) {
        for (const { section, formField } of sectionMap) {
          const encryptedKey = configData.routing_config[section]?.['X-API-Key'];
          if (encryptedKey) {
            try {
              const decryptedKey = await decryptData(encryptedKey);
              if (decryptedKey) {
                decryptedApiKeys[formField] = decryptedKey;
                debug.log(config, `Clé ${section} décryptée et importée`);
              }
            } catch (error) {
              debug.error(config, `Erreur lors du décryptage de ${section}:`, error);
              showError(t('CONFIG', 'CONFIG_DECRYPT_ERROR', `Error decrypting ${section} API key`));
            }
          }
        }
      }
      
      // Fallback: décrypter les clés API encryptées legacy si elles existent
      const encryptedKeys = [
        { fileKey: 'text_ai_api_key_encrypted', formField: 'textAiApiKey' },
        { fileKey: 'vision_ai_api_key_encrypted', formField: 'visionAiApiKey' },
        { fileKey: 'translation_ai_api_key_encrypted', formField: 'translationAiApiKey' }
      ];
      
      for (const keyInfo of encryptedKeys) {
        if (configData[keyInfo.fileKey] && !decryptedApiKeys[keyInfo.formField]) {
          try {
            const decryptedKey = await decryptData(configData[keyInfo.fileKey]);
            if (decryptedKey) {
              decryptedApiKeys[keyInfo.formField] = decryptedKey;
              debug.log(config, `Clé legacy ${keyInfo.fileKey} décryptée et importée`);
            }
          } catch (error) {
            debug.error(config, `Erreur lors du décryptage de ${keyInfo.fileKey}:`, error);
            showError(t('CONFIG', 'CONFIG_DECRYPT_ERROR', `Error decrypting ${keyInfo.fileKey}`));
          }
        }
      }
      
      // Mettre à jour le formulaire avec les nouvelles valeurs
      setValue('uiLanguage', configData.ui_language || language);
      setValue('textAiApiKey', decryptedApiKeys.textAiApiKey || '');
      setValue('visionAiApiKey', decryptedApiKeys.visionAiApiKey || '');
      setValue('translationAiApiKey', decryptedApiKeys.translationAiApiKey || '');
      
      // Restaurer les modèles IA et la langue de travail depuis routing_config
      if (configData.routing_config) {
        // Modèles IA
        if (configData.routing_config.text?.['X-Model']) {
          setValue('textAiModel', configData.routing_config.text['X-Model']);
          debug.log(config, 'Modèle Text IA restauré depuis routing_config');
        }
        if (configData.routing_config.vision?.['X-Model']) {
          setValue('visionAiModel', configData.routing_config.vision['X-Model']);
          debug.log(config, 'Modèle Vision IA restauré depuis routing_config');
        }
        if (configData.routing_config.translation?.['X-Model']) {
          setValue('translationAiModel', configData.routing_config.translation['X-Model']);
          debug.log(config, 'Modèle Translation IA restauré depuis routing_config');
        }
        
        // Fallback sur legacy si nécessaire
        if (!configData.routing_config.text?.['X-Model']) {
          setValue('textAiModel', configData.text_ai_model_key || '');
        }
        if (!configData.routing_config.vision?.['X-Model']) {
          setValue('visionAiModel', configData.vision_ai_model_key || '');
        }
        if (!configData.routing_config.translation?.['X-Model']) {
          setValue('translationAiModel', configData.translation_ai_model_key || '');
        }
      } else {
        // Si pas de routing_config du tout, utiliser les données legacy
        setValue('textAiModel', configData.text_ai_model_key || '');
        setValue('visionAiModel', configData.vision_ai_model_key || '');
        setValue('translationAiModel', configData.translation_ai_model_key || '');
      }
      
      // Langue de travail IA depuis la nouvelle structure routing_config
      const workingLanguage = configData.routing_config?.text?.['X-Working-Language'];
      if (workingLanguage) {
        setValue('aiWorkingLanguage', workingLanguage);
        debug.log(config, `Langue de travail IA restaurée: ${workingLanguage}`);
      }
      
      // Note: Les champs World Anvil utilisent useLocalStorage dans useWorldAnvilLists
      // donc ils se mettront à jour automatiquement via les données localStorage
      // Les données WA ont déjà été restaurées dans localStorage ci-dessus
      
      // Forcer le rafraîchissement des composants en déclenchant des événements storage
      // Cela permettra aux hooks useLocalStorage de détecter les changements
      const waKeys = ['wa_world', 'wa_manuscript', 'wa_version', 'bel_tags', 'bel_shortcuts', 'bel_formats', 'wa_label_generated', 'wa_label_copied', 'wa_label_canon'];
      waKeys.forEach(key => {
        // Déclencher l'événement storage standard (pour les autres onglets)
        window.dispatchEvent(new StorageEvent('storage', {
          key: key,
          newValue: localStorage.getItem(key),
          oldValue: null
        }));
        
        // Déclencher l'événement personnalisé (pour le même onglet)
        window.dispatchEvent(new Event('local-storage-change'));
      });
      
      // Changer la langue de l'interface si nécessaire
      if (configData.ui_language && configData.ui_language !== language) {
        changeLanguage(configData.ui_language);
      }

      // Déclencher les tests des circuit breakers pour les nouvelles clés chargées
      if (decryptedApiKeys.textAiApiKey) {
        debug.log(config, 'Test de la clé Text AI après chargement...');
        getCircuitBreaker('/api/ia/text').checkHealth(HEALTHCHECK_MODE.USER);
      }
      if (decryptedApiKeys.visionAiApiKey) {
        debug.log(config, 'Test de la clé Vision AI après chargement...');
        getCircuitBreaker('/api/ia/vision').checkHealth(HEALTHCHECK_MODE.USER);
      }
      if (decryptedApiKeys.translationAiApiKey) {
        debug.log(config, 'Test de la clé Translation AI après chargement...');
        getCircuitBreaker('/api/ia/translation').checkHealth(HEALTHCHECK_MODE.USER);
      }

      showSuccess(t('CONFIG', 'CONFIG_FILE_LOADED_ENCRYPTED', 'Encrypted configuration loaded from file'));
      debug.log(config, 'Configuration encryptée chargée avec succès', {
        encryptedKeys: Object.keys(decryptedApiKeys).length
      });
    } catch (error) {
      showError(t('CONFIG', 'CONFIG_LOAD_ERROR', 'Error loading configuration file'));
    }
    
    // Réinitialiser l'input file
    fileInput.value = '';
  };
  
  reader.onerror = () => {
    showError(t('CONFIG', 'CONFIG_LOAD_ERROR', 'Error reading configuration file'));
    fileInput.value = '';
  };
  
  reader.readAsText(file);
};
